import { Suspense, useEffect, useLayoutEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Outlet } from 'react-router-dom';
import { LoginInfoDto } from '@asset-metrix/portal-gateway-client-js';
import {
  getConfiguredLanguageCodes,
  resolvePreferredLanguage,
} from '@asset-metrix/portal-ui';
import { useFavicon } from '@mantine/hooks';
import { Notifications } from '@mantine/notifications';
import * as Sentry from '@sentry/react';
import { QueryErrorResetBoundary } from '@tanstack/react-query';

import { MaintenanceModeActiveError } from '../api/errors';
import { useLoginInfoSuspenseQuery } from '../api/portalGateway/loginInfo';
import { FullScreenError } from '../components/fullscreenframe/FullScreenError';
import { FullScreenLoader } from '../components/fullscreenframe/FullScreenLoader';
import { useMaintenanceModeUnlock } from '../hooks/useMaintenanceModeUnlock';
import { useSessionLifecycle } from '../hooks/useSessionLifecycle';
import { useVegaFunctions } from '../hooks/useVegaFunctions';
import i18n from '../i18n/config';
import { useStore } from '../store';
import { getFavIconUrl } from '../utils/getFavIconUrl';
import { getTenantNameBySubdomain } from '../utils/getTenantNameBySubdomain';

import {
  applyThemeVariables,
  useColorScheme,
  usePortalTheme,
} from './usePortalTheme';

// Ensures that login info is fetched and session is initialized
export function AppInit() {
  const tenantFromHost = getTenantNameBySubdomain();

  useLayoutEffect(() => {
    useStore.getState().setTenantName(tenantFromHost);
  }, [tenantFromHost]);

  // Use suspense query to fetch login info and initialize session
  const loginInfoQuery = useLoginInfoSuspenseQuery();

  return <AppThemeAndLocale loginInfoData={loginInfoQuery.data} />;
}

export function AppThemeAndLocale({
  loginInfoData,
}: {
  loginInfoData: LoginInfoDto;
}) {
  const {
    theme,
    localeMessages,
    languages = [],
    isMaintenanceModeActive,
  } = loginInfoData;

  const [colorScheme, setColorScheme] = useColorScheme();

  // set timer for token refresh, initSession needs to have been called before.
  useSessionLifecycle();

  // always use active theme if user isn't allowed to switch themes
  useEffect(() => {
    if (
      theme &&
      colorScheme !== theme.activeTheme &&
      theme.userCanSwitchTheme === false
    ) {
      setColorScheme(theme.activeTheme);
    }
  }, [colorScheme, setColorScheme, theme]);

  useEffect(() => {
    if (localeMessages) {
      // An empty i18n list must not collapse the allowed set to the tenant
      // default, or a stored language (for example en) is dropped.
      const language = resolvePreferredLanguage(
        localeMessages,
        languages.length > 0 ? getConfiguredLanguageCodes(languages) : undefined
      );
      console.debug('Changing i18next language to %s', language);
      void i18n.changeLanguage(language);
    }
  }, [languages, localeMessages]);
  // Apply tenant-specific theme when login info is available
  const { activeTheme } = usePortalTheme();

  useEffect(() => {
    if (activeTheme) {
      applyThemeVariables(activeTheme, colorScheme);
    }
  }, [activeTheme, colorScheme]);

  useFavicon(getFavIconUrl(activeTheme?.['--portal-ui-favicon']));

  useVegaFunctions();

  const [maintenanceModeUnlocked] = useMaintenanceModeUnlock();

  useEffect(() => {
    if (isMaintenanceModeActive && !maintenanceModeUnlocked) {
      // Throw error here to trigger the error boundary and show the fullscreen error in the same way as a failing request would do.
      throw new MaintenanceModeActiveError();
    }
  }, [isMaintenanceModeActive, maintenanceModeUnlocked]);

  return <Outlet />;
}

export function App() {
  return (
    <>
      <Notifications />
      <Suspense fallback={<FullScreenLoader />}>
        <QueryErrorResetBoundary>
          {({ reset }) => (
            <ErrorBoundary
              FallbackComponent={FullScreenError}
              onError={(error) => {
                Sentry.captureException(error);
              }}
              onReset={reset}
            >
              <AppInit />
            </ErrorBoundary>
          )}
        </QueryErrorResetBoundary>
      </Suspense>
    </>
  );
}

export default App;
